/**
 * Guida in-app: impaginazione condivisa fra la guida admin del Centro Ticket
 * (`/admin/guida-ticket`) e quella dei rappresentanti
 * (`/area-associativa/guida-ticket`).
 *
 * Rende: intestazione, riepilogo a link numerati, sezioni numerate con ancora,
 * riquadri "Da sapere" (verde) e "Attenzione" (ambra), immagini in
 * `figure/figcaption` ingrandibili, e un "Torna al riepilogo" a fine sezione.
 *
 * `variant` cambia SOLO le classi dei contenitori: l'admin usa le card V2
 * (`a2-card`, dentro `.adm2`), l'area associativa le classi Tailwind della sua
 * shell. Testi, dimensioni e struttura sono identici, cosi le due guide non
 * divergono.
 *
 * Gli screenshot arrivano dopo i contenuti: un file mancante non deve lasciare
 * l'icona di immagine rotta, quindi `onError` toglie l'`img` e resta il solo
 * testo alternativo.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ChevronUp, Info } from 'lucide-react';
import { GUIDE_IMAGE_BASE, type GuideBlock, type GuideSection } from './guideModel';
import '../../../styles/support-guide.css';

export type GuideVariant = 'admin' | 'associative';

export interface GuidePageProps {
  title: string;
  intro: string[];
  sections: GuideSection[];
  variant?: GuideVariant;
  /** Contenuto fra l'intestazione e il riepilogo (es. la card del video guida). */
  lead?: React.ReactNode;
}

/** Id del riquadro riepilogo: e la destinazione dei "torna su". */
const SUMMARY_ID = 'riepilogo';

/** Le uniche differenze fra le due varianti: il vestito delle card. */
const CARD_CLASS: Record<GuideVariant, string> = {
  admin: 'a2-card p-4 sm:p-5',
  associative:
    'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-5',
};

const TEXT = 'text-sm leading-relaxed text-gray-700 dark:text-gray-200';
const MUTED = 'text-sm text-gray-500 dark:text-gray-400';

interface GuideImageFigureProps {
  src: string;
  alt: string;
  caption?: string;
  onZoom: (src: string, alt: string) => void;
}

const GuideImageFigure: React.FC<GuideImageFigureProps> = ({ src, alt, caption, onZoom }) => {
  const [broken, setBroken] = useState(false);

  // File non ancora caricato: si mostra il testo alternativo, mai il segnaposto
  // di immagine rotta del browser.
  if (broken) {
    return (
      <figure className="my-3">
        <figcaption className={MUTED}>{caption || alt}</figcaption>
      </figure>
    );
  }

  return (
    <figure className="my-3">
      <button
        type="button"
        className="block w-full text-left"
        onClick={() => onZoom(src, alt)}
        title="Ingrandisci l'immagine"
      >
        <img
          src={`${GUIDE_IMAGE_BASE}${src}`}
          alt={alt}
          loading="lazy"
          onError={() => setBroken(true)}
          className="max-w-full h-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm"
        />
      </button>
      <figcaption className={`${MUTED} mt-1.5`}>{caption || alt}</figcaption>
    </figure>
  );
};

const Callout: React.FC<{ kind: 'tip' | 'warn'; title?: string; text: string }> = ({
  kind,
  title,
  text,
}) => {
  const tip = kind === 'tip';
  const box = tip
    ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20'
    : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20';
  const ink = tip
    ? 'text-emerald-900 dark:text-emerald-200'
    : 'text-amber-900 dark:text-amber-200';
  const icon = tip
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-amber-600 dark:text-amber-400';

  return (
    <div className={`my-3 rounded-xl border p-3 flex items-start gap-2.5 ${box}`}>
      <span className={`mt-0.5 shrink-0 ${icon}`} aria-hidden="true">
        {tip ? <Info className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
      </span>
      <div className={`text-sm leading-relaxed ${ink}`}>
        <span className="font-semibold">{title || (tip ? 'Da sapere' : 'Attenzione')}. </span>
        {text}
      </div>
    </div>
  );
};

const Block: React.FC<{ block: GuideBlock; onZoom: (src: string, alt: string) => void }> = ({
  block,
  onZoom,
}) => {
  switch (block.type) {
    case 'p':
      return <p className={`${TEXT} mt-2`}>{block.text}</p>;
    case 'ul':
      return (
        <ul className={`${TEXT} mt-2 list-disc pl-5 space-y-1`}>
          {block.items.map((it) => (
            <li key={it}>{it}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol className={`${TEXT} mt-2 list-decimal pl-5 space-y-1`}>
          {block.items.map((it) => (
            <li key={it}>{it}</li>
          ))}
        </ol>
      );
    case 'tip':
      return <Callout kind="tip" title={block.title} text={block.text} />;
    case 'warn':
      return <Callout kind="warn" title={block.title} text={block.text} />;
    case 'img':
      return (
        <GuideImageFigure src={block.src} alt={block.alt} caption={block.caption} onZoom={onZoom} />
      );
    default:
      return null;
  }
};

const GuidePage: React.FC<GuidePageProps> = ({ title, intro, sections, variant = 'admin', lead }) => {
  const card = CARD_CLASS[variant];
  const [zoom, setZoom] = useState<{ src: string; alt: string } | null>(null);

  const openZoom = useCallback((src: string, alt: string) => setZoom({ src, alt }), []);
  const closeZoom = useCallback(() => setZoom(null), []);

  // Escape chiude l'immagine ingrandita: e l'unico livello sopra la pagina.
  useEffect(() => {
    if (!zoom) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeZoom();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [zoom, closeZoom]);

  return (
    <div className="sg mx-auto w-full max-w-4xl px-4 py-6">
      <header className="mb-4">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h1>
        {intro.map((t) => (
          <p key={t} className={`${TEXT} mt-2`}>
            {t}
          </p>
        ))}
      </header>

      {lead}

      {/* Riepilogo: in stampa non serve, il documento e gia tutto in pagina. */}
      <nav
        id={SUMMARY_ID}
        aria-label="Indice"
        className={`${card} sg-anchor sg-noprint mb-4`}
      >
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">In questa guida</h2>
        <ol className="mt-2 space-y-1 list-decimal pl-5">
          {sections.map((s) => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                className="text-sm text-emerald-700 dark:text-emerald-400 hover:underline"
              >
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <div className="space-y-4">
        {sections.map((s, i) => (
          // `aria-labelledby` sul titolo: la sezione diventa un punto di
          // riferimento con un nome, raggiungibile anche saltando fra le
          // regioni con un lettore di schermo.
          <section
            key={s.id}
            id={s.id}
            aria-labelledby={`${s.id}-titolo`}
            className={`${card} sg-anchor`}
          >
            <h2
              id={`${s.id}-titolo`}
              className="text-base font-semibold text-gray-900 dark:text-white"
            >
              {i + 1}. {s.title}
            </h2>
            {s.blocks.map((b, j) => (
              // I blocchi di una sezione sono una lista statica e ordinata:
              // l'indice e una chiave stabile quanto il contenuto.
              // eslint-disable-next-line react/no-array-index-key
              <Block key={`${s.id}-${j}`} block={b} onZoom={openZoom} />
            ))}
            <p className="mt-3 sg-noprint">
              <a
                href={`#${SUMMARY_ID}`}
                className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:underline"
              >
                <ChevronUp className="w-3 h-3" aria-hidden="true" /> Torna al riepilogo
              </a>
            </p>
          </section>
        ))}
      </div>

      {zoom && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={zoom.alt}
          className="sg-lightbox sg-noprint fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          {/* Il fondo e un bottone a tutta pagina: un clic ovunque chiude. */}
          <button
            type="button"
            className="absolute inset-0 w-full h-full cursor-zoom-out"
            aria-label="Chiudi l'immagine"
            onClick={closeZoom}
          />
          <img
            src={`${GUIDE_IMAGE_BASE}${zoom.src}`}
            alt={zoom.alt}
            className="relative max-w-full max-h-full rounded-lg shadow-lg"
          />
        </div>
      )}
    </div>
  );
};

export default GuidePage;
